// src/components/AuthView.tsx
import React, { useEffect, useMemo, useState } from 'react';
import {
  GoogleAuthProvider,
  OAuthProvider,
  createUserWithEmailAndPassword,
  signInAnonymously,
  getRedirectResult,
  signInWithEmailAndPassword,
  signInWithPopup,
  signInWithRedirect,
  signOut,
  sendPasswordResetEmail,
  setPersistence,
  indexedDBLocalPersistence,
  browserLocalPersistence,
  browserSessionPersistence,
  updateProfile,
  type AuthError,
} from 'firebase/auth';
import { auth, functions } from '../lib/firebase';
import { httpsCallable } from 'firebase/functions';
import { cfBootstrapMemberAndAccess } from '../services/accessClient';
import { updateUserProfile } from '../services/users';
import { bridgeJsAuthWithGoogleResult, ensureJsAuthFromNative } from '../services/authBridge';
import { RoddoLogo } from './icons';
import {
  buildRoddoAgeEligibilityRecord,
  checkRoddoAgeEligibility,
  isRoddoEligibleAgeBracket,
  RODDO_AGE_BRACKET_KEY,
  RODDO_AGE_GATE_BLOCKED_KEY,
  RODDO_AGE_GATE_PASSED_KEY,
} from '../utils/ageEligibility';

import { Capacitor } from '@capacitor/core';
import { FirebaseAuthentication } from '@capacitor-firebase/authentication';

type Phase = 'idle' | 'checking-redirect' | 'signing-in' | 'creating-account' | 'error';
type Mode = 'signin' | 'signup';
type ProviderKind = 'google' | 'apple';

type PublicAccessConfig = {
  blockNewSignups: boolean;
  waitlistEnabled: boolean;
};

const REMEMBER_ME_KEY = 'roddoRememberMe';
const INVITE_CODE_KEY = 'roddo_invite_code';
const PENDING_DISPLAY_NAME_KEY = 'roddo_pending_display_name';
const AUTH_INTENT_KEY = 'roddo_auth_intent';

// In-memory bridge so App.tsx does not run a no-code access check while AuthView
// is still redeeming signup access. This is not the source of truth;
// the Cloud Function remains the only authority.
declare global {
  interface Window {
    __roddoSignupAccessBootstrapPending?: boolean;
  }
}

function isNative(): boolean {
  try {
    return Capacitor.isNativePlatform();
  } catch {
    return false;
  }
}

function getNativePlatform(): string {
  try {
    return Capacitor.getPlatform();
  } catch {
    return 'web';
  }
}

function isNativeIOS(): boolean {
  return isNative() && getNativePlatform() === 'ios';
}

function isIOSWeb(): boolean {
  if (typeof navigator === 'undefined') return false;
  return (
    /iPad|iPhone|iPod/.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && (navigator as any).maxTouchPoints > 1)
  );
}

function isSafariWeb(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  return /Safari/.test(ua) && !/Chrome|Chromium|Edg|OPR|CriOS|FxiOS/.test(ua);
}

function isAppleFamilyBrowser(): boolean {
  return isIOSWeb() || isSafariWeb();
}

function friendlyAuthError(e: unknown): string {
  const err = e as Partial<AuthError> & { message?: string };
  const code = String((err as any)?.code || '');

  if (code.includes('popup-blocked')) return 'Popup was blocked. We’ll use redirect sign-in instead.';
  if (code.includes('popup-closed-by-user')) return 'Popup was closed before finishing. Try again.';
  if (code.includes('cancelled-popup-request')) return 'Sign-in was interrupted. Try again.';
  if (code.includes('unauthorized-domain')) return 'This domain is not authorized for sign-in in Firebase.';
  if (code.includes('operation-not-allowed')) return 'That sign-in method is not enabled for this Firebase project.';
  if (code.includes('network-request-failed')) return 'Network error while signing in. Check your connection and retry.';
  if (code.includes('argument-error')) return 'Sign-in configuration error. Please retry (or update app).';
  if (code.includes('invalid-credential')) return 'That email or password is incorrect.';
  if (code.includes('invalid-login-credentials')) return 'That email or password is incorrect.';
  if (code.includes('user-not-found')) return 'No account was found for that email.';
  if (code.includes('wrong-password')) return 'That email or password is incorrect.';
  if (code.includes('email-already-in-use')) return 'An account already exists for that email.';
  if (code.includes('weak-password')) return 'Password is too weak. Use at least 6 characters.';
  if (code.includes('invalid-email')) return 'Please enter a valid email address.';
  if (code.includes('missing-password')) return 'Please enter your password.';

  const msg = String(err?.message || '');
  const lowerMsg = msg.toLowerCase();

  if (
    lowerMsg.includes('no credentials available') ||
    lowerMsg.includes('google_credential_missing') ||
    lowerMsg.includes('credential_missing')
  ) {
    return 'Google sign-in did not return a usable credential. On the emulator, open Play Store first and make sure a Google account is signed in, then try Continue with Google again.';
  }

  return msg || 'Sign-in failed. Please try again.';
}


function detailedAuthError(e: unknown): string {
  const friendly = friendlyAuthError(e);
  const raw = e as { code?: unknown; message?: unknown; name?: unknown; stack?: unknown };
  const code = raw?.code == null ? '' : String(raw.code);
  const name = raw?.name == null ? '' : String(raw.name);
  const message = raw?.message == null ? '' : String(raw.message);

  const lines: string[] = [];

  if (message.trim().startsWith('10:') || code === '10') {
    lines.push(
      'Android Google sign-in returned error 10 (DEVELOPER_ERROR). This usually means the installed app signing certificate does not match the Firebase/Google OAuth Android client for com.roddo.app.',
    );
  } else {
    lines.push(friendly);
  }

  lines.push('');
  lines.push('Debug details:');
  if (code) lines.push(`code: ${code}`);
  if (name) lines.push(`name: ${name}`);
  if (message) lines.push(`message: ${message}`);

  try {
    const json = JSON.stringify(e, null, 2);
    if (json && json !== '{}') {
      lines.push('raw:');
      lines.push(json);
    }
  } catch {
    // Ignore non-serializable plugin errors.
  }

  return lines.filter(Boolean).join('\n');
}

function makeProvider(kind: ProviderKind) {
  if (kind === 'apple') {
    const provider = new OAuthProvider('apple.com');
    provider.addScope('email');
    provider.addScope('name');
    return provider;
  }

  const provider = new GoogleAuthProvider();
  provider.setCustomParameters({ prompt: 'select_account' });
  return provider;
}

function withTimeout<T>(promise: Promise<T>, ms: number, label: string): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    const timer = window.setTimeout(() => {
      reject(new Error(`${label}_timeout_after_${ms}ms`));
    }, ms);

    promise.then(
      (value) => {
        window.clearTimeout(timer);
        resolve(value);
      },
      (err) => {
        window.clearTimeout(timer);
        reject(err);
      },
    );
  });
}

function nativeGoogleDebugSummary(result: any): string {
  const credential = result?.credential ?? {};
  const user = result?.user ?? {};

  return JSON.stringify({
    hasCredential: !!result?.credential,
    hasIdToken: !!(credential?.idToken ?? result?.idToken ?? result?.authentication?.idToken),
    hasAccessToken: !!(credential?.accessToken ?? result?.accessToken ?? result?.authentication?.accessToken),
    uid: user?.uid ?? null,
    email: user?.email ?? null,
  });
}

export default function AuthView() {
  const [phase, setPhase] = useState<Phase>('checking-redirect');
  const [error, setError] = useState<string | null>(null);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [lastTried, setLastTried] = useState<'popup' | 'redirect' | 'native' | 'email' | null>(null);
  const [nativeDebug, setNativeDebug] = useState<string | null>(null);

  const [mode, setMode] = useState<Mode>('signin');
  const [rememberMe, setRememberMe] = useState<boolean>(() => {
    try {
      return window.localStorage.getItem(REMEMBER_ME_KEY) !== 'false';
    } catch {
      return true;
    }
  });
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [birthMonth, setBirthMonth] = useState('');
  const [birthYear, setBirthYear] = useState('');
  const [ageGateMessage, setAgeGateMessage] = useState<string | null>(null);
  const [ageGateRequested, setAgeGateRequested] = useState(false);
  const [ageGatePassed, setAgeGatePassed] = useState<boolean>(() => {
    try { return window.localStorage.getItem(RODDO_AGE_GATE_PASSED_KEY) === '1'; } catch { return false; }
  });
  const [ageGateBlocked, setAgeGateBlocked] = useState<boolean>(() => {
    try { return window.localStorage.getItem(RODDO_AGE_GATE_BLOCKED_KEY) === '1'; } catch { return false; }
  });
  const [inviteCode, setInviteCode] = useState<string>(() => {
    try {
      return (window.localStorage.getItem(INVITE_CODE_KEY) || '').toUpperCase();
    } catch {
      return '';
    }
  });
  const [accessConfig, setAccessConfig] = useState<PublicAccessConfig>({
    blockNewSignups: false,
    waitlistEnabled: true,
  });
  const [accessConfigLoading, setAccessConfigLoading] = useState(true);

  const normalizedInviteCode = inviteCode.trim().toUpperCase();
  const nativeIOS = isNativeIOS();
  const showCreateAccount = !nativeIOS;
  // Roddo currently has two signup states only:
  // open or paused. Promo codes remain optional and never gate signup.
  const signupBlocked =
    mode === 'signup' && accessConfig.blockNewSignups;
  const signupReady = !signupBlocked;

  const persistBetaAuthIntent = (nextMode: Mode) => {
    try {
      window.localStorage.setItem(AUTH_INTENT_KEY, nextMode);
      window.sessionStorage.setItem(AUTH_INTENT_KEY, nextMode);

      const cleanName = fullName.trim();
      if (nextMode === 'signup') {
        window.__roddoSignupAccessBootstrapPending = true;
        if (normalizedInviteCode) {
          window.localStorage.setItem(INVITE_CODE_KEY, normalizedInviteCode);
          window.sessionStorage.setItem(INVITE_CODE_KEY, normalizedInviteCode);
          window.dispatchEvent(
            new CustomEvent('roddo:invite-code-ready', {
              detail: { code: normalizedInviteCode },
            }),
          );
        }
        if (cleanName) {
          window.localStorage.setItem(PENDING_DISPLAY_NAME_KEY, cleanName);
          window.sessionStorage.setItem(PENDING_DISPLAY_NAME_KEY, cleanName);
        }
      }

      // Existing users should not accidentally reuse an old signup code.
      // Only clear when they intentionally choose the sign-in path.
      if (nextMode === 'signin') {
        window.localStorage.removeItem(INVITE_CODE_KEY);
        window.sessionStorage.removeItem(INVITE_CODE_KEY);
        window.localStorage.removeItem(PENDING_DISPLAY_NAME_KEY);
        window.sessionStorage.removeItem(PENDING_DISPLAY_NAME_KEY);
      }
    } catch {
      // Private browsing / webview storage can fail. Backend still enforces access after auth.
    }
  };

  const clearStoredSignupHandoff = () => {
    try {
      window.localStorage.removeItem(INVITE_CODE_KEY);
      window.sessionStorage.removeItem(INVITE_CODE_KEY);
      window.localStorage.removeItem(AUTH_INTENT_KEY);
      window.sessionStorage.removeItem(AUTH_INTENT_KEY);
    } catch {}
  };

  const bootstrapAccessAfterSignup = async (code = '') => {
    const cleanCode = code.trim().toUpperCase();
    window.__roddoSignupAccessBootstrapPending = true;

    try {
      // Open signup: every authenticated user is bootstrapped as an active member.
      // A supplied promo code is optional metadata and never gates account creation.
      const res: any = await withTimeout(
        cfBootstrapMemberAndAccess(
          cleanCode ? { inviteCode: cleanCode } : {},
        ),
        20000,
        'access_bootstrap',
      );

      const data = res?.data || {};
      const status = String(data?.accessStatus || '').toLowerCase();
      const approved = data?.ok === true && (status === 'active' || status === 'member');

      if (!approved) {
        throw new Error(
          String(data?.error || 'Roddo could not activate this account yet. Please try again.'),
        );
      }

      clearStoredSignupHandoff();
      window.dispatchEvent(new CustomEvent('roddo:access-granted'));
      return true;
    } finally {
      // The persisted signup intent is only a redirect handoff marker. Always
      // clear it when this attempt settles so Safari cannot remain trapped on
      // the access-loading screen after a timeout or soft callable failure.
      try {
        window.localStorage.removeItem(AUTH_INTENT_KEY);
        window.sessionStorage.removeItem(AUTH_INTENT_KEY);
      } catch {}

      window.__roddoSignupAccessBootstrapPending = false;
      window.dispatchEvent(new CustomEvent('roddo:access-bootstrap-finished'));
    }
  };

  const confirmAgeEligibility = () => {
    setAgeGateMessage(null);
    const result = checkRoddoAgeEligibility(birthMonth, birthYear);

    if (!result.valid || !result.bracket) {
      setAgeGateMessage('Enter a valid birth month and four-digit birth year.');
      return;
    }

    if (result.bracket === 'U13') {
      setAgeGateBlocked(true);
      setAgeGatePassed(false);
      setBirthMonth('');
      setBirthYear('');
      try {
        window.localStorage.removeItem(RODDO_AGE_GATE_PASSED_KEY);
        window.localStorage.removeItem(RODDO_AGE_BRACKET_KEY);
        window.localStorage.setItem(RODDO_AGE_GATE_BLOCKED_KEY, '1');
      } catch {}
      setAgeGateMessage('Based on the age entered, Roddo account creation is unavailable.');
      return;
    }

    setAgeGatePassed(true);
    setAgeGateBlocked(false);
    setBirthMonth('');
    setBirthYear('');
    try {
      window.localStorage.setItem(RODDO_AGE_GATE_PASSED_KEY, '1');
      window.localStorage.setItem(RODDO_AGE_BRACKET_KEY, result.bracket);
      window.localStorage.removeItem(RODDO_AGE_GATE_BLOCKED_KEY);
    } catch {}
  };

  const getEligibleBracketForProfile = () => {
    try {
      const value = window.localStorage.getItem(RODDO_AGE_BRACKET_KEY);
      return isRoddoEligibleAgeBracket(value) ? value : null;
    } catch {
      return null;
    }
  };

  const buildAgeEligibilityProfilePatch = () => {
    const bracket = getEligibleBracketForProfile();
    if (!bracket) return null;
    const verifiedAt = Date.now();
    return {
      ageBracket: bracket,
      ageVerifiedAt: verifiedAt,
      ageEligibility: buildRoddoAgeEligibilityRecord(bracket, verifiedAt),
    };
  };

  const persistAgeEligibilityForUid = async (uid: string) => {
    const patch = buildAgeEligibilityProfilePatch();
    if (!patch) return;
    await updateUserProfile(uid, patch as any);
  };

  const requireAgeEligibility = () => {
    if (ageGatePassed && !ageGateBlocked) return true;
    setAgeGateRequested(true);
    setAgeGateMessage(
      ageGateBlocked
        ? 'Based on the age previously entered, Roddo account creation is unavailable.'
        : 'Complete the age check before creating a new Roddo account or continuing as Guest.',
    );
    return false;
  };

  const requireInviteBeforeSignup = () => {
    if (mode !== 'signup') return true;

    if (accessConfig.blockNewSignups) {
      setPhase('error');
      setError('New Roddo account creation is currently paused. Existing anglers can still sign in.');
      return false;
    }

    persistBetaAuthIntent('signup');
    return true;
  };

  const { preferRedirect, modeLabel } = useMemo(() => {
    if (isNative()) return { preferRedirect: false, modeLabel: 'Native mode: secure sign-in on device' };
    if (isIOSWeb()) return { preferRedirect: true, modeLabel: 'iPhone / iPad web: Redirect sign-in for reliability' };
    if (isSafariWeb()) return { preferRedirect: false, modeLabel: 'Safari desktop: Popup-first (anti-loop)' };
    return { preferRedirect: false, modeLabel: 'Popup preferred when allowed' };
  }, []);
  useEffect(() => {
    let alive = true;

    (async () => {
      try {
        const fn = httpsCallable<Record<string, never>, PublicAccessConfig>(
          functions,
          'getPublicAccessConfig',
        );
        const res = await withTimeout(
          fn({}),
          10000,
          'public_access_config',
        );

        if (!alive) return;
        const data = (res?.data || {}) as Partial<PublicAccessConfig>;
        setAccessConfig({
          blockNewSignups: data.blockNewSignups === true,
          waitlistEnabled: data.waitlistEnabled !== false,
        });
      } catch (err) {
        console.warn('[AuthView] public access config unavailable; using open-signup fallback', err);
      } finally {
        if (alive) setAccessConfigLoading(false);
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    // Apple review builds should not expose invite-code account creation.
    // Reviewers sign in with the supplied demo account or an existing account,
    // and iOS still presents Sign in with Apple as the equivalent login option.
    if (nativeIOS && mode === 'signup') {
      setMode('signin');
      setError(null);
      setNativeDebug(null);
    }
  }, [nativeIOS, mode]);


  useEffect(() => {
    let alive = true;

    (async () => {
      setPhase('checking-redirect');
      setError(null);

      if (isNative()) {
        if (!alive) return;
        setPhase('idle');
        return;
      }

      try {
        const res = await getRedirectResult(auth);
        if (!alive) return;
        if (res?.user) {
          const code =
            window.sessionStorage.getItem(INVITE_CODE_KEY) ||
            window.localStorage.getItem(INVITE_CODE_KEY) ||
            normalizedInviteCode;
          const authIntent =
            window.sessionStorage.getItem(AUTH_INTENT_KEY) ||
            window.localStorage.getItem(AUTH_INTENT_KEY) ||
            'signin';
          try {
            // Provider sign-in may create a brand-new Firebase user even when the
            // UI was in "Sign in" mode (notably native Sign in with Apple).
            // Bootstrap is idempotent, so always heal/confirm Roddo access here.
            await bootstrapAccessAfterSignup(code || '');
            if (authIntent === 'signup') {
              await persistAgeEligibilityForUid(res.user.uid);
            }
          } catch (accessErr) {
            if (!alive) return;
            setPhase('error');
            setError(friendlyAuthError(accessErr));
            return;
          }
          setPhase('idle');
          return;
        }
        setPhase('idle');
      } catch (e) {
        if (!alive) return;
        console.warn('[AuthView] getRedirectResult error (often ok):', e);
        setPhase('idle');
      }
    })();

    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    try {
      window.localStorage.setItem(REMEMBER_ME_KEY, rememberMe ? 'true' : 'false');
    } catch {
      // Storage can be unavailable in private modes; Firebase persistence still gets attempted.
    }
  }, [rememberMe]);

  const applyAuthPersistence = async () => {
    // Remember Me must be applied before every sign-in attempt.
    // Native builds prefer IndexedDB, but older/locked-down WebViews can reject it,
    // so fall back to browser local persistence instead of silently becoming memory-only.
    if (!rememberMe) {
      await setPersistence(auth, browserSessionPersistence);
      return;
    }

    const durableOrder = isNative()
      ? [indexedDBLocalPersistence, browserLocalPersistence]
      : [browserLocalPersistence, indexedDBLocalPersistence];

    let lastErr: unknown = null;

    for (const persistence of durableOrder) {
      try {
        await setPersistence(auth, persistence);
        return;
      } catch (err) {
        lastErr = err;
        console.warn('[AuthView] auth persistence option failed; trying fallback', err);
      }
    }

    throw lastErr ?? new Error('auth_persistence_unavailable');
  };


  const applyGuestPersistence = async () => {
    // Guest identity should survive app/browser restarts regardless of the
    // Remember Me checkbox. Firebase remains the source of truth.
    const durableOrder = isNative()
      ? [indexedDBLocalPersistence, browserLocalPersistence]
      : [browserLocalPersistence, indexedDBLocalPersistence];

    let lastErr: unknown = null;

    for (const persistence of durableOrder) {
      try {
        await setPersistence(auth, persistence);
        return;
      } catch (err) {
        lastErr = err;
        console.warn('[AuthView] guest persistence option failed; trying fallback', err);
      }
    }

    throw lastErr ?? new Error('guest_persistence_unavailable');
  };

  const doNativeGoogle = async () => {
    setLastTried('native');
    setPhase('signing-in');
    setError(null);
    setNativeDebug('Opening Google account picker…');

    await applyAuthPersistence();

    // Native shell + native Firebase Auth + JS Firebase auth bridge.
    //
    // Important: do NOT force skipNativeAuth:true here. That creates a JS-only
    // session, so iOS/Android have no durable native user to restore on relaunch.
    // We let the plugin complete native Firebase Auth, then bridge that durable
    // native user into JS auth for Firestore/Functions.
    const googleResult = await withTimeout(
      FirebaseAuthentication.signInWithGoogle({
        skipNativeAuth: false,
        useCredentialManager: false,
      } as any),
      30000,
      'native_google_picker',
    );

    console.debug('[AuthView] Native Google result:', nativeGoogleDebugSummary(googleResult));
    setNativeDebug('Google returned a result. Syncing Roddo account…');

    const nativeCurrent = await withTimeout(
      FirebaseAuthentication.getCurrentUser(),
      15000,
      'native_get_current_user_after_google',
    );
    const expectedNativeUid =
      nativeCurrent?.user?.uid ??
      (googleResult as any)?.user?.uid ??
      null;

    const bridged = await bridgeJsAuthWithGoogleResult(
      googleResult,
      expectedNativeUid,
      'AuthView.nativeGoogle',
    );

    const uid = auth.currentUser?.uid ?? null;
    console.debug('[AuthView] Firebase JS bridge complete:', {
      bridged,
      uid,
      expectedNativeUid,
    });

    if (!bridged || !uid) {
      throw new Error('native_js_auth_bridge_failed');
    }
    await bootstrapAccessAfterSignup(normalizedInviteCode);
    const googleAgePatch = mode === 'signup' ? buildAgeEligibilityProfilePatch() : null;
    await updateUserProfile(uid, {
      accountType: 'registered',
      authProviders: ['google.com'],
      accountUpgradedAt: Date.now(),
      ...(googleAgePatch || {}),
    } as any).catch(() => {});
    setNativeDebug(uid ? `Firebase sign-in complete (${uid}). Loading Roddo…` : 'Firebase sign-in complete. Loading Roddo…');
    setPhase('idle');
  };

  const doNativeApple = async () => {
    setLastTried('native');
    setPhase('signing-in');
    setError(null);
    setNativeDebug('Opening Apple sign-in…');

    await applyAuthPersistence();

    // Native iOS Sign in with Apple satisfies App Store Guideline 4.8.
    // Keep native Firebase Auth durable, then bridge native -> JS auth so
    // Firestore/Functions continue to see the Firebase JS user.
    await withTimeout(
      FirebaseAuthentication.signInWithApple({
        skipNativeAuth: false,
      } as any),
      30000,
      'native_apple_picker',
    );

    setNativeDebug('Apple returned a result. Syncing Roddo account…');

    const nativeCurrent = await withTimeout(
      FirebaseAuthentication.getCurrentUser(),
      15000,
      'native_get_current_user_after_apple',
    );
    const expectedNativeUid = nativeCurrent?.user?.uid ?? null;

    const bridged = await ensureJsAuthFromNative('AuthView.nativeApple');
    const uid = auth.currentUser?.uid ?? null;

    console.debug('[AuthView] Native Apple bridge complete:', {
      bridged,
      uid,
      expectedNativeUid,
    });

    if (!bridged || !uid) {
      throw new Error('native_apple_js_auth_bridge_failed');
    }

    await bootstrapAccessAfterSignup(normalizedInviteCode);
    const appleAgePatch = mode === 'signup' ? buildAgeEligibilityProfilePatch() : null;
    await updateUserProfile(uid, {
      accountType: 'registered',
      authProviders: ['apple.com'],
      accountUpgradedAt: Date.now(),
      ...(appleAgePatch || {}),
    } as any).catch(() => {});

    setNativeDebug(uid ? `Apple sign-in complete (${uid}). Loading Roddo…` : 'Apple sign-in complete. Loading Roddo…');
    setPhase('idle');
  };

  const doRedirect = async (kind: ProviderKind) => {
    setLastTried('redirect');
    setPhase('signing-in');
    setError(null);

    await applyAuthPersistence();
    const provider = makeProvider(kind);
    await signInWithRedirect(auth, provider);
  };

  const doPopupThenFallback = async (kind: ProviderKind) => {
    setLastTried('popup');
    setPhase('signing-in');
    setError(null);

    await applyAuthPersistence();
    const provider = makeProvider(kind);

    try {
      const cred = await signInWithPopup(auth, provider);
      await bootstrapAccessAfterSignup(normalizedInviteCode);
      const popupAgePatch = mode === 'signup' ? buildAgeEligibilityProfilePatch() : null;
      await updateUserProfile(cred.user.uid, {
        accountType: 'registered',
        authProviders: Array.from(new Set((cred.user.providerData || []).map((row) => row.providerId).filter(Boolean))),
        accountUpgradedAt: Date.now(),
        ...(popupAgePatch || {}),
      } as any).catch(() => {});
      setPhase('idle');
    } catch (e) {
      const msg = friendlyAuthError(e);
      console.warn(`[AuthView] ${kind} popup sign-in error:`, e);

      const code = String((e as any)?.code || '');
      const shouldFallback =
        code.includes('popup') ||
        code.includes('network-request-failed') ||
        code.includes('internal-error');

      if (shouldFallback) {
        setError(msg + ' Redirecting…');
        try {
          await doRedirect(kind);
          return;
        } catch (e2) {
          setPhase('error');
          setError(detailedAuthError(e2));
          return;
        }
      }

      setPhase('error');
      setError(msg);
    }
  };

  const onProviderSignIn = async (kind: ProviderKind) => {
    // Provider sign-in can create a new Firebase account when no account exists,
    // so age-screen before handing any personal information to the provider flow.
    if (mode === 'signup' && !requireAgeEligibility()) return;
    if (!requireInviteBeforeSignup()) return;

    try {
      persistBetaAuthIntent(mode);
      if (isNative()) {
        if (kind === 'google') {
          await doNativeGoogle();
          return;
        }

        if (kind === 'apple' && nativeIOS) {
          await doNativeApple();
          return;
        }

        setPhase('error');
        setError('Apple sign-in is available on iPhone/iPad. Please use Google or Email on this device.');
        return;
      }

      // Firebase redirect auth is reliable on Roddo's hosted first-party domain,
      // but localhost/dev returns through the firebaseapp.com auth helper and can
      // lose the redirect handoff in modern browser storage/privacy modes. Keep
      // local development popup-first so auth can be tested without a redirect
      // loop. Production web uses redirect to avoid Chrome COOP popup noise.
      const isLocalDevHost =
        import.meta.env.DEV ||
        window.location.hostname === 'localhost' ||
        window.location.hostname === '127.0.0.1';

      if (kind === 'google' && isLocalDevHost) {
        await doPopupThenFallback(kind);
      } else if (preferRedirect || kind === 'apple' || kind === 'google') {
        await doRedirect(kind);
      } else {
        await doPopupThenFallback(kind);
      }
    } catch (e) {
      setPhase('error');
      setError(detailedAuthError(e));
    }
  };

  const onEmailSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLastTried('email');
    setError(null);

    const cleanEmail = email.trim();
    const cleanName = fullName.trim();

    if (!cleanEmail) {
      setPhase('error');
      setError('Please enter your email.');
      return;
    }

    if (!password) {
      setPhase('error');
      setError('Please enter your password.');
      return;
    }

    try {
      if (mode === 'signup') {
        if (!requireAgeEligibility()) return;
        if (!requireInviteBeforeSignup()) return;
        setPhase('creating-account');
        await applyAuthPersistence();
        const cred = await createUserWithEmailAndPassword(auth, cleanEmail, password);
        await persistAgeEligibilityForUid(cred.user.uid);

        if (cleanName) {
          try {
            window.localStorage.setItem(PENDING_DISPLAY_NAME_KEY, cleanName);
            window.sessionStorage.setItem(PENDING_DISPLAY_NAME_KEY, cleanName);
          } catch {}

          try {
            await updateProfile(cred.user, { displayName: cleanName });
          } catch (profileErr) {
            console.warn('[AuthView] updateProfile after signup failed:', profileErr);
          }
        }

        await bootstrapAccessAfterSignup(normalizedInviteCode);
        setPhase('idle');
        return;
      }

      persistBetaAuthIntent('signin');
      setPhase('signing-in');
      await applyAuthPersistence();
      const cred = await signInWithEmailAndPassword(auth, cleanEmail, password);
      await bootstrapAccessAfterSignup('');
      await updateUserProfile(cred.user.uid, {
        accountType: 'registered',
        authProviders: ['password'],
        accountUpgradedAt: Date.now(),
      } as any).catch(() => {});
      setPhase('idle');
    } catch (err) {
      setPhase('error');
      setError(friendlyAuthError(err));
    }
  };

  const onForgotPassword = async () => {
    const cleanEmail = email.trim();
    setError(null);
    setStatusMessage(null);

    if (!cleanEmail) {
      setPhase('error');
      setError('Enter your email above, then tap Forgot password again.');
      return;
    }

    try {
      setPhase('signing-in');
      await sendPasswordResetEmail(auth, cleanEmail);
      setStatusMessage('Password reset email sent. Check your inbox and spam folder.');
      setPhase('idle');
    } catch (err) {
      if (String((err as any)?.code || '').includes('user-not-found')) {
        setStatusMessage('If that address belongs to a Roddo account, a password reset email is on the way.');
        setPhase('idle');
        return;
      }
      setPhase('error');
      setError(friendlyAuthError(err));
    }
  };

  const onGuestContinue = async () => {
    if (!requireAgeEligibility()) return;

    if (accessConfig.blockNewSignups) {
      setPhase('error');
      setError('New Roddo account creation is currently paused.');
      return;
    }

    try {
      setLastTried('email');
      setPhase('creating-account');
      setError(null);

      // Guest identity is intentionally durable on this installation,
      // independent of the regular Remember Me toggle.
      await applyGuestPersistence();

      let uid: string | null = null;
      let createdNewGuest = false;

      if (isNative()) {
        const nativeApi: any = FirebaseAuthentication as any;

        // Reuse an existing native Firebase session before ever creating
        // another anonymous user.
        const existingNative: any = await withTimeout(
          nativeApi.getCurrentUser(),
          10000,
          'native_guest_restore',
        );
        
        const existingNativeUid = existingNative?.user?.uid ?? null;

        if (existingNativeUid) {
          console.debug('[AuthView] restoring existing native Firebase user', {
            uid: existingNativeUid,
          });

          const bridged = await ensureJsAuthFromNative('AuthView.guest.restore');
          uid = auth.currentUser?.uid ?? existingNativeUid;

          if (!bridged || !uid) {
            throw new Error('guest_restore_js_auth_bridge_failed');
          }
        } else {
          if (typeof nativeApi.signInAnonymously !== 'function') {
            throw new Error('native_guest_auth_unavailable');
          }

          await withTimeout(
            nativeApi.signInAnonymously(),
            20000,
            'native_guest_auth',
          );

          createdNewGuest = true;

          const nativeCurrent = await nativeApi.getCurrentUser();
          uid = nativeCurrent?.user?.uid ?? null;

          const bridged = await ensureJsAuthFromNative('AuthView.guest.create');
          uid = auth.currentUser?.uid ?? uid;

          if (!bridged || !uid) {
            throw new Error('guest_js_auth_bridge_failed');
          }
        }
      } else {
        // Wait for Firebase to hydrate persisted IndexedDB/localStorage auth
        // before deciding whether a new anonymous account is necessary.
        const authWithReady = auth as typeof auth & {
          authStateReady?: () => Promise<void>;
        };

        if (typeof authWithReady.authStateReady === 'function') {
          await authWithReady.authStateReady();
        }

        const existing = auth.currentUser;

        if (existing) {
          uid = existing.uid;

          console.debug('[AuthView] restoring existing web Firebase user', {
            uid,
            anonymous: existing.isAnonymous,
          });
        } else {
          const cred = await signInAnonymously(auth);
          uid = cred.user.uid;
          createdNewGuest = true;
        }
      }

      if (!uid) {
        throw new Error('guest_uid_missing');
      }

      await bootstrapAccessAfterSignup('');

      if (createdNewGuest) {
        const guestAgePatch = buildAgeEligibilityProfilePatch();
        await updateUserProfile(uid, {
          accountType: 'guest',
          authProviders: [],
          guestCreatedAt: Date.now(),
          guestLastSeenAt: Date.now(),
          ...(guestAgePatch || {}),
        } as any);
      } else if (auth.currentUser?.isAnonymous) {
        // Returning guest: preserve the original guestCreatedAt timestamp.
        const returningGuestAgePatch = buildAgeEligibilityProfilePatch();
        await updateUserProfile(uid, {
          accountType: 'guest',
          authProviders: [],
          guestLastSeenAt: Date.now(),
          ...(returningGuestAgePatch || {}),
        } as any);
      }

      setPhase('idle');
    } catch (err) {
      setPhase('error');
      setError(friendlyAuthError(err));
    }
  };

  const retry = async () => {
    setError(null);
    setNativeDebug(null);
    setPhase('idle');

    if (!isNative() && lastTried === 'popup') {
      await doRedirect('google');
      return;
    }
  };

  const busy = phase === 'checking-redirect' || phase === 'signing-in' || phase === 'creating-account';
  const showApple = nativeIOS || (!isNative() && isAppleFamilyBrowser());

  const switchMode = (nextMode: Mode) => {
    setMode(nextMode);
    if (nextMode === 'signin') setAgeGateRequested(false);
    setError(null);
    setStatusMessage(null);
    setNativeDebug(null);
  };

  const fieldClass =
    'roddo-mobile-form w-full rounded-[12px] border border-[#4A3A2A]/15 bg-[#FFFCF7] px-3.5 py-3 text-[15px] font-semibold text-[#30271E] placeholder:text-[#8A7C6D] outline-none transition focus:border-[#2F80ED]/55 focus:ring-4 focus:ring-[#2F80ED]/10';

  return (
    <div
      data-roddo-auth-root="true"
      className="
        fixed inset-0 z-[1200]
        h-[100dvh] min-h-0 w-full overflow-y-auto overflow-x-hidden
        overscroll-y-contain bg-[#F7F2E8] text-[#30271E]
        touch-pan-y [-webkit-overflow-scrolling:touch]
      "
      style={{
        scrollPaddingTop: 'max(env(safe-area-inset-top), 16px)',
        scrollPaddingBottom: 'calc(var(--roddo-keyboard-h, 0px) + max(env(safe-area-inset-bottom), 24px))',
      }}
    >
      <div
        data-roddo-auth-wrap="true"
        className="
          mx-auto flex min-h-full w-full max-w-[520px] items-start justify-center
          px-3 pb-[max(env(safe-area-inset-bottom),1rem)]
          pt-[max(env(safe-area-inset-top),0.75rem)]
          sm:px-5 sm:py-8
        "
      >
        <main
          data-roddo-auth-card="true"
          className="
            w-full overflow-hidden rounded-[20px]
            border border-[#4A3A2A]/15 bg-[#FFFCF7]
            shadow-[0_18px_46px_rgba(74,58,42,0.12)]
          "
        >
          <div data-roddo-auth-body="true" className="px-5 pb-5 pt-5 sm:px-7 sm:pb-7 sm:pt-6">
            <div className="flex justify-center">
              <div data-roddo-auth-logo-wrap="true"><RoddoLogo className="h-[70px] w-auto object-contain sm:h-[78px]" /></div>
            </div>

            <div className="mt-3 text-center">
              <div className="text-[10px] font-black uppercase tracking-[0.24em] text-[#2F80ED]">
                Explore. Log. Connect.
              </div>
              <h1 className="mt-2 text-[25px] font-black tracking-[-0.035em] text-[#30271E]">
                {mode === 'signin' ? 'Welcome to Roddo' : 'Create your Roddo account'}
              </h1>
              <p className="mx-auto mt-2 max-w-[360px] text-sm leading-5 text-[#6E6255]">
                {mode === 'signin'
                  ? 'Sign in to pick up your maps, catches, Roddo Reads, crews, and fishing history.'
                  : 'Create one account for your water, catches, gear, crews, and tournaments.'}
              </p>
            </div>

            {showCreateAccount && mode === 'signup' && accessConfigLoading ? (
              <div className="mt-4 rounded-[12px] border border-[#4A3A2A]/12 bg-[#F2EBE1] px-3.5 py-3 text-center text-xs font-bold text-[#6E6255]">
                Checking current signup access…
              </div>
            ) : null}

            {showCreateAccount && mode === 'signup' && signupBlocked ? (
              <div className="mt-4 rounded-[14px] border border-red-500/20 bg-red-50 px-4 py-3">
                <div className="text-[11px] font-black uppercase tracking-[0.16em] text-red-700">
                  New signup paused
                </div>
                <p className="mt-1 text-sm leading-5 text-red-800/80">
                  Roddo is not accepting new accounts right now. Existing anglers can still sign in.
                </p>
              </div>
            ) : null}

            {(mode === 'signup' || ageGateRequested) && (!ageGatePassed || ageGateBlocked) ? (
              <div className="mt-4 rounded-[14px] border border-[#4A3A2A]/12 bg-[#F2EBE1] px-4 py-4">
                <div className="text-[11px] font-black uppercase tracking-[0.16em] text-[#2F80ED]">Age check</div>
                <p className="mt-1 text-[12px] font-semibold leading-5 text-[#6E6255]">
                  Enter your birth month and year before creating an account or using Google, Apple, or Guest. Roddo uses this only to determine age eligibility and does not save the entered month/year as your date of birth.
                </p>
                {!ageGateBlocked ? (
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <select
                      value={birthMonth}
                      onChange={(e) => setBirthMonth(e.target.value)}
                      className={fieldClass}
                      aria-label="Birth month"
                    >
                      <option value="">Birth month</option>
                      {Array.from({ length: 12 }, (_, index) => index + 1).map((month) => (
                        <option key={month} value={month}>{new Date(2000, month - 1, 1).toLocaleString(undefined, { month: 'long' })}</option>
                      ))}
                    </select>
                    <input
                      value={birthYear}
                      onChange={(e) => setBirthYear(e.target.value.replace(/\D/g, '').slice(0, 4))}
                      inputMode="numeric"
                      autoComplete="bday-year"
                      placeholder="Birth year"
                      aria-label="Birth year"
                      className={fieldClass}
                    />
                  </div>
                ) : null}
                {ageGateMessage ? (
                  <div className={`mt-3 rounded-[10px] px-3 py-2 text-[11px] font-bold leading-4 ${ageGateBlocked ? 'border border-red-500/20 bg-red-50 text-red-800' : 'bg-white/70 text-[#6E6255]'}`}>
                    {ageGateMessage}
                  </div>
                ) : null}
                {!ageGateBlocked ? (
                  <button
                    type="button"
                    onClick={confirmAgeEligibility}
                    disabled={busy}
                    className="mt-3 min-h-10 w-full rounded-[11px] bg-[#2F80ED] px-4 py-2.5 text-sm font-black text-white transition active:translate-y-px disabled:opacity-50"
                  >
                    Continue
                  </button>
                ) : null}
              </div>
            ) : null}

            <div className="roddo-auth-provider-stack mt-5 grid gap-2.5">
              <button
                type="button"
                onClick={() => onProviderSignIn('google')}
                disabled={busy || (mode === 'signup' && (!ageGatePassed || ageGateBlocked || !signupReady))}
                className="
                  min-h-12 w-full rounded-[12px] border border-[#4A3A2A]/18
                  bg-white px-4 py-3 text-sm font-black text-[#30271E]
                  shadow-[0_4px_12px_rgba(74,58,42,0.06)]
                  transition hover:bg-[#FAF7F1] active:translate-y-px
                  disabled:cursor-not-allowed disabled:opacity-55
                "
              >
                {phase === 'checking-redirect'
                  ? 'Checking sign-in…'
                  : phase === 'signing-in'
                    ? 'Signing you in…'
                    : 'Continue with Google'}
              </button>

              {showApple ? (
                <button
                  type="button"
                  onClick={() => onProviderSignIn('apple')}
                  disabled={busy || (mode === 'signup' && (!ageGatePassed || ageGateBlocked || !signupReady))}
                  className="
                    min-h-12 w-full rounded-[12px] border border-black
                    bg-black px-4 py-3 text-sm font-black text-white
                    transition hover:bg-black/90 active:translate-y-px
                    disabled:cursor-not-allowed disabled:opacity-55
                  "
                >
                  Continue with Apple
                </button>
              ) : null}
            </div>

            <div data-roddo-auth-guest="true" className="mt-2.5">
              <button
                type="button"
                onClick={onGuestContinue}
                disabled={busy || ageGateBlocked || accessConfig.blockNewSignups}
                className="
                  min-h-11 w-full rounded-[12px] border border-[#4A3A2A]/16
                  bg-[#F2EBE1] px-4 py-2.5 text-sm font-black text-[#4F4338]
                  transition hover:bg-[#ECE3D8] active:translate-y-px
                  disabled:cursor-not-allowed disabled:opacity-50
                "
              >
                Continue as Guest
              </button>
              <p className="mt-2 text-center text-[11px] leading-4 text-[#8A7C6D]">
                Start with a private Roddo profile. Link Apple, Google, or email later without losing your catches.
              </p>
            </div>

            {nativeDebug ? (
              <div className="mt-3 rounded-[12px] border border-[#2F80ED]/15 bg-[#2F80ED]/[0.06] px-4 py-3 text-xs font-semibold leading-5 text-[#31587C]">
                {nativeDebug}
              </div>
            ) : null}

            <div className="roddo-auth-divider my-5 flex items-center gap-3">
              <div className="h-px flex-1 bg-[#4A3A2A]/12" />
              <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#8A7C6D]">
                or use email
              </span>
              <div className="h-px flex-1 bg-[#4A3A2A]/12" />
            </div>

            <form onSubmit={onEmailSubmit} className="roddo-auth-form grid gap-3">
              {mode === 'signup' ? (
                <label className="grid gap-1.5">
                  <span className="text-xs font-black text-[#5F5448]">Display name</span>
                  <input
                    type="text"
                    autoComplete="name"
                    value={fullName}
                    onChange={(e) => setFullName(e.target.value)}
                    placeholder="Captain Kim"
                    className={fieldClass}
                  />
                </label>
              ) : null}

              <label className="grid gap-1.5">
                <span className="text-xs font-black text-[#5F5448]">Email</span>
                <input
                  type="email"
                  autoComplete="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className={fieldClass}
                />
              </label>

              <label className="grid gap-1.5">
                <span className="text-xs font-black text-[#5F5448]">Password</span>
                <div className="relative">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    autoComplete={mode === 'signup' ? 'new-password' : 'current-password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder={mode === 'signup' ? 'Create a password' : 'Enter your password'}
                    className={`${fieldClass} pr-12`}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword((visible) => !visible)}
                    className="absolute inset-y-0 right-0 flex w-12 items-center justify-center text-[#6E6255] transition hover:text-[#2F80ED] focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#2F80ED]/45"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                    aria-pressed={showPassword}
                    title={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 3l18 18" />
                        <path d="M10.6 10.7a2 2 0 002.7 2.7" />
                        <path d="M9.9 4.2A10.7 10.7 0 0112 4c5.5 0 9 5 9 8a11.8 11.8 0 01-2.1 3.5" />
                        <path d="M6.6 6.6C4.4 8.1 3 10.2 3 12c0 3 3.5 8 9 8a9.7 9.7 0 004.1-.9" />
                      </svg>
                    ) : (
                      <svg aria-hidden="true" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M2.8 12s3.4-6 9.2-6 9.2 6 9.2 6-3.4 6-9.2 6-9.2-6-9.2-6z" />
                        <circle cx="12" cy="12" r="2.5" />
                      </svg>
                    )}
                  </button>
                </div>
              </label>

              {mode === 'signin' ? (
                <div className="flex items-center justify-between gap-3">
                  <label className="inline-flex min-h-9 items-center gap-2 text-xs font-bold text-[#6E6255]">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      className="h-4 w-4 accent-[#2F80ED]"
                    />
                    Remember me
                  </label>
                  <button
                    type="button"
                    onClick={onForgotPassword}
                    disabled={busy}
                    className="min-h-9 px-1 text-xs font-black text-[#2F80ED] transition hover:opacity-75 disabled:opacity-50"
                  >
                    Forgot password?
                  </button>
                </div>
              ) : null}

              <button
                type="submit"
                disabled={busy || (mode === 'signup' && !signupReady)}
                className="
                  mt-1 min-h-12 w-full rounded-[12px] bg-[#2F80ED]
                  px-4 py-3 text-sm font-black text-white
                  shadow-[0_7px_16px_rgba(47,128,237,0.18)]
                  transition hover:brightness-105 active:translate-y-px
                  disabled:cursor-not-allowed disabled:opacity-55
                "
              >
                {phase === 'creating-account'
                  ? 'Creating account…'
                  : phase === 'signing-in'
                    ? 'Signing in…'
                    : mode === 'signup'
                      ? 'Create account'
                      : 'Sign in'}
              </button>
            </form>

            {showCreateAccount && mode === 'signup' && !signupBlocked ? (
              <div className="mt-4 rounded-[14px] border border-[#4A3A2A]/12 bg-[#F7F2E8] p-4">
                <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#2F80ED]">
                  Promo code · optional
                </div>
                <p className="mt-1 text-xs leading-5 text-[#6E6255]">
                  Add a Roddo promo or partner code if you have one. You can create your account without it.
                </p>
                <input
                  type="text"
                  autoComplete="one-time-code"
                  value={inviteCode}
                  onChange={(e) => {
                    setInviteCode(e.target.value.toUpperCase().replace(/\s+/g, ''));
                    setError(null);
                  }}
                  placeholder="ENTER PROMO CODE"
                  className={`${fieldClass} mt-3 text-center font-black uppercase tracking-[0.14em]`}
                />
              </div>
            ) : null}

            {showCreateAccount ? (
              <div data-roddo-auth-switch="true" className="mt-5 text-center">
                <button
                  type="button"
                  onClick={() => switchMode(mode === 'signin' ? 'signup' : 'signin')}
                  disabled={busy}
                  className="min-h-10 px-3 text-sm font-black text-[#2F80ED] transition hover:opacity-75 disabled:opacity-50"
                >
                  {mode === 'signin'
                    ? 'New to Roddo? Create account'
                    : 'Already have an account? Sign in'}
                </button>
              </div>
            ) : (
              <p className="mt-5 text-center text-xs leading-5 text-[#6E6255]">
                Sign in with your Roddo review/demo account or use Apple/Google to continue.
              </p>
            )}

            {error ? (
              <div className="mt-4 rounded-[14px] border border-red-500/20 bg-red-50 p-4">
                <div className="text-sm font-black text-red-800">Sign-in issue</div>
                <pre className="mt-2 max-h-[min(240px,calc(var(--roddo-visual-vh,100dvh)-260px))] overflow-y-auto whitespace-pre-wrap break-words text-xs leading-5 text-red-800/85 [-webkit-overflow-scrolling:touch]">
                  {error}
                </pre>
                <div className="mt-3 flex flex-wrap justify-center gap-2">
                  <button
                    type="button"
                    onClick={retry}
                    className="rounded-[10px] border border-red-700/15 bg-white px-4 py-2 text-sm font-black text-red-800"
                  >
                    Retry
                  </button>
                  {!isNative() ? (
                    <button
                      type="button"
                      onClick={() => doRedirect('google')}
                      className="rounded-[10px] bg-[#30271E] px-4 py-2 text-sm font-black text-white"
                    >
                      Use Google Redirect
                    </button>
                  ) : null}
                </div>
              </div>
            ) : null}

            {statusMessage ? (
              <div className="mt-4 rounded-[14px] border border-[#2F80ED]/20 bg-[#2F80ED]/[0.07] p-4 text-sm font-bold leading-5 text-[#31587C]">
                {statusMessage}
              </div>
            ) : null}
          </div>

          <div className="border-t border-[#4A3A2A]/10 bg-[#F7F2E8]/70 px-5 py-3 text-center text-[10px] font-bold tracking-[0.05em] text-[#8A7C6D]">
            Secure Roddo sign-in
          </div>
        </main>
      </div>
    </div>
  );
}
